import { type ApolloClient, type NormalizedCacheObject, gql } from '@apollo/client/core';
import { Query } from '../../generated/api.graphql';
import * as VPErrors from '../../VPError';
import type { VPResult } from '../VPResult';
import { VP_ERROR_FIELDS } from '../VPError/fragments';
import { TEAM_ROLE_DETAILS_FIELDS } from '../App/TeamRole/fragments';
import * as TeamRoleDetails from '../App/TeamRole/TeamRoleDetails';
import * as UserPermission from '../App/Permission/UserPermission';
import { forAccount } from '../accountContext';
import * as ApiKey from './ApiKey';
import * as ApiKeyInput from './ApiKeyInput';
import * as PermissionScope from './PermissionScope';

const API_KEY_FIELDS = gql`
  fragment ApiKeyFields on ApiKey {
    id
    name
    key
    expiresAt
    createdAt
    webhookSubscriptionCount
    account {
      id
      name
    }
    permissionSet {
      permissionGroups {
        icon
        key
        label
        permissions {
          icon
          key
          label
          level
          options {
            label
            value
          }
        }
      }
      allowances {
        enabled
        key
        label
      }
    }
  }
`;

const API_KEY_QUERY = gql`
  query ApiKey {
    me {
      apiKeys {
        ...ApiKeyFields
      }
    }
  }
  ${API_KEY_FIELDS}
`;

export const fetchApiKeys = async (client: ApolloClient<NormalizedCacheObject>): Promise<ApiKey.ApiKey[]> => {
  const { data } = await client.query({
    query: API_KEY_QUERY,
    fetchPolicy: 'network-only',
  });

  return data.me.apiKeys.map(ApiKey.fromGQL);
};

// `id` is selected so this stays safe to cache: `Me` is normalized by it, and without it a
// cached write would land inline over `ROOT_QUERY.currentUser`, replacing the profile's entry.
export const API_KEY_SCOPE_QUERY = gql`
  query ApiKeyScope {
    currentUser {
      id
      permissions {
        key
        level
      }
    }
    teamRoles {
      id
    }
  }
`;

export const TEAM_ROLE_DETAILS_QUERY = gql`
  query apiKeyTeamRoleDetails($roleId: Int!) {
    teamRoleDetails(roleId: $roleId) {
      ...TeamRoleDetailsFields
    }
  }
  ${TEAM_ROLE_DETAILS_FIELDS}
`;

// The matrix the caller may grant in `accountId`, which need not be the account the connection
// header names. `no-cache` because the grant is account-scoped while `currentUser.permissions`
// takes no account argument, so a cached copy would misread as the switcher's.
export const fetchPermissionScope = async (
  client: ApolloClient<NormalizedCacheObject>,
  accountId: number,
): Promise<PermissionScope.PermissionScope> => {
  const context = forAccount(accountId);

  const { data } = await client.query<Query>({
    query: API_KEY_SCOPE_QUERY,
    fetchPolicy: 'no-cache',
    context,
  });

  if (!data?.currentUser?.permissions) {
    throw new Error('Failed to read your own permissions for this account.');
  }

  const roleId = data.teamRoles?.[0]?.id;
  if (!roleId) {
    throw new Error('This account has no team roles to read the permission list from.');
  }

  const details = await client.query<Query>({
    query: TEAM_ROLE_DETAILS_QUERY,
    variables: { roleId },
    fetchPolicy: 'no-cache',
    context,
  });

  if (!details.data?.teamRoleDetails) {
    throw new Error('Failed to read the permission list for this account.');
  }

  return PermissionScope.cappedBy(
    TeamRoleDetails.fromGql(details.data.teamRoleDetails),
    UserPermission.fromGqlCollection(data.currentUser.permissions),
  );
};

export const API_KEY_CREATE_MUTATION = gql`
  mutation CreateApiKey($apiKeyInput: ApiKeyAttributes!) {
    createApiKey(input: { apiKey: $apiKeyInput }) {
      apiKey {
        id
        name
        key
        expiresAt
        createdAt
        keyUnmasked
      }
    }
  }
`;

export const createApiKey = async (
  client: ApolloClient<NormalizedCacheObject>,
  apiKeyInput: ApiKeyInput.ApiKeyInput,
): Promise<ApiKey.ApiKeyFull> => {
  // `no-cache`: the response carries the secret, and a normalized copy would outlive the one
  // screen allowed to show it.
  const { data } = await client.mutate({
    mutation: API_KEY_CREATE_MUTATION,
    variables: {
      apiKeyInput: ApiKeyInput.toGqlInput(apiKeyInput),
    },
    context: forAccount(apiKeyInput.accountId),
    fetchPolicy: 'no-cache',
  });

  return ApiKey.fromGQL<ApiKey.ApiKeyFull>(data.createApiKey.apiKey);
};

export const API_KEY_REVOKE_MUTATION = gql`
  mutation RevokeApiKey($id: Int!) {
    revokeApiKey(input: { id: $id }) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

// No account context: the key's account is pinned server-side, so naming one here would be
// the caller asserting a scope it does not get to choose.
export const revokeApiKey = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<void, VPErrors.VPError[]>> => {
  const { data } = await client.mutate({
    mutation: API_KEY_REVOKE_MUTATION,
    variables: { id },
  });

  const payload = data?.revokeApiKey;
  if (!payload) {
    throw new Error('revokeApiKey returned no payload');
  }

  // Gated on `success`, not on `errors`: the payload always carries an errors array.
  if (!payload.success) {
    return { success: false, errors: VPErrors.fromGqlCollection(payload.errors) };
  }

  return { success: true, data: undefined };
};

export const API_KEY_ROTATE_MUTATION = gql`
  mutation RotateApiKey($id: Int!) {
    rotateApiKey(input: { id: $id }) {
      apiKey {
        id
        name
        key
        expiresAt
        createdAt
        keyUnmasked
      }
    }
  }
`;

/** Replaces the key with one carrying the same scope; the old one stops authenticating at once. */
export const rotateApiKey = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<ApiKey.ApiKeyFull> => {
  // `no-cache` for the same reason as create: the response carries a secret that cannot be
  // asked for again, and a normalized copy would outlive the screen allowed to show it.
  const { data } = await client.mutate({
    mutation: API_KEY_ROTATE_MUTATION,
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  // The payload has no `success` and no `errors` — an absent key is the only failure shape.
  const rotated = data?.rotateApiKey?.apiKey;
  if (!rotated) {
    throw new Error('rotateApiKey returned no replacement key');
  }

  return ApiKey.fromGQL<ApiKey.ApiKeyFull>(rotated);
};
